export interface IRemoteDevice {
    get DeviceGuid(): string;
    get Channel(): number;
    get Guid(): string;
    get Name(): string;
    get RemoteChannelAmount(): number;
    get DeviceChannelAmount(): number;
    get DeviceType(): DeviceType;
    get RemoteMode(): RemoteMode;
    set RemoteMode(value: RemoteMode);
    get CurrentRemotePairingOptions(): DeviceTypeOptions;
    get RawCurrentRemoteOptions(): PairingOption;
}

export enum RemoteMode {
    OnOff = 1,
    Bistable = 2,
    Monostable = 3,
    Timer = 4,
    Local = 5,
    Central = 6,
    PixoBidi_1 = 7,
    PixoBidi_12 = 8,
    TwoButton = 9,
    OneButton = 10,
    Comfort = 11,
    TimerDimmer = 12,
    Gate = 13,
    GateWay = 14,
    NXZFBidi = 15,
    PXZFBidi = 16,
    WSZFBidi = 17
}

export enum DeviceType {
    OnOff = 1,
    Blind = 2,
    Dimmer = 3,
    Gate = 4,
    Gateway = 5,
    Facade = 6
}

//Remote modes
type OnOff = {
    [RemoteMode.OnOff]: RemoteMode,
    [RemoteMode.Bistable]: RemoteMode,
    [RemoteMode.Monostable]: RemoteMode,
    [RemoteMode.Timer]: RemoteMode;
}

type Blind = {
    [RemoteMode.PixoBidi_1]: RemoteMode,
    [RemoteMode.PixoBidi_12]: RemoteMode,
}

type Dimmer = {
    [RemoteMode.TwoButton]: RemoteMode,
    [RemoteMode.OneButton]: RemoteMode,
    [RemoteMode.Comfort]: RemoteMode,
    [RemoteMode.TimerDimmer]: RemoteMode,
}

type Gate = {
    [RemoteMode.Gate]: RemoteMode;
    [RemoteMode.Bistable]: RemoteMode;
}

type Gateway = {
    [RemoteMode.GateWay]: RemoteMode;
    [RemoteMode.Bistable]: RemoteMode;
}

type Facade = {
    [RemoteMode.NXZFBidi]: RemoteMode;
    [RemoteMode.PXZFBidi]: RemoteMode;
    [RemoteMode.WSZFBidi]: RemoteMode;
}

type DeviceTypeRemoteMode = {
    [DeviceType.OnOff]: OnOff,
    [DeviceType.Blind]: Blind,
    [DeviceType.Dimmer]: Dimmer,
    [DeviceType.Gate]: Gate,
    [DeviceType.Gateway]: Gateway,
    [DeviceType.Facade]: Facade,
}

export class DeviceTypeMode {
    static readonly AvailableModes: DeviceTypeRemoteMode = {
        [DeviceType.OnOff]: {
            [RemoteMode.OnOff]: RemoteMode.OnOff,
            [RemoteMode.Bistable]: RemoteMode.Bistable,
            [RemoteMode.Monostable]: RemoteMode.Monostable,
            [RemoteMode.Timer]: RemoteMode.Timer
        },
        [DeviceType.Blind]: {
            [RemoteMode.PixoBidi_1]: RemoteMode.PixoBidi_1,
            [RemoteMode.PixoBidi_12]: RemoteMode.PixoBidi_12,
        },
        [DeviceType.Dimmer]: {
            [RemoteMode.TwoButton]: RemoteMode.TwoButton,
            [RemoteMode.OneButton]: RemoteMode.OneButton,
            [RemoteMode.Comfort]: RemoteMode.Comfort,
            [RemoteMode.TimerDimmer]: RemoteMode.TimerDimmer,
        },
        [DeviceType.Gate]: {
            [RemoteMode.Gate]: RemoteMode.Gate,
            [RemoteMode.Bistable]: RemoteMode.Bistable
        },
        [DeviceType.Gateway]: {
            [RemoteMode.GateWay]: RemoteMode.GateWay,
            [RemoteMode.Bistable]: RemoteMode.Bistable
        },
        [DeviceType.Facade]: {
            [RemoteMode.NXZFBidi]: RemoteMode.NXZFBidi,
            [RemoteMode.PXZFBidi]: RemoteMode.PXZFBidi,
            [RemoteMode.WSZFBidi]: RemoteMode.WSZFBidi
        }
    }
}

class RemoteDeviceResponse {
    DeviceGuid: string = "";
    Channel: number = 0;
    Guid: string = "";
    Name: string = "";
    RemoteChannelAmount: number = 0;
    DeviceChannelAmount: number = 0;
    PairingOption: PairingOption = new PairingOption();
    OldSettingsOptional: PairingOption = new PairingOption();
    DeviceType: DeviceType = DeviceType.OnOff;
}

class PairingOption {
    Mode: RemoteMode = RemoteMode.Bistable;
    KeyOn: number = 0;
    KeyOff: number = 0;
    KeyBrightening: number = 0;
    KeyDimmer: number = 0;
    TimeOn: number = 0;
    Channel: number = 0;
}

type DeviceTypeOptions = {
    [DeviceType.OnOff]: OnOffOptions,
    [DeviceType.Blind]: BlindOptions,
    [DeviceType.Dimmer]: DimmerOptions,
    [DeviceType.Gate]: GateOptions,
    [DeviceType.Gateway]: GateWayOptions,
    [DeviceType.Facade]: FacadeOptions,
}

interface IOptions {
    ModeParams: any
}

//Params
class OnOffParameters {
    private _remoteChannels: number;
    private _keyOn: number;
    private _keyOff: number;

    constructor(remoteChannels: number, options: PairingOption) {
        this._remoteChannels = remoteChannels;
        this._keyOn = options.KeyOn;
        this._keyOff = options.KeyOff;
    }

    get KeyOn(): number {
        return this._keyOn;
    }
    get KeyOff(): number {
        return this._keyOff;
    }
    set KeyOn(value: number) {
        this._keyOn = value;
    }
    set KeyOff(value: number) {
        this._keyOff = value;
    }
}

class ChannelParameters {
    private _remoteChannels: number;
    private _channel: number;

    constructor(remoteChannels: number, options: PairingOption) {
        this._remoteChannels = remoteChannels;
        this._channel = options.Channel;
    }

    get Channel(): number {
        return this._channel
    }
    set Channel(value: number) {
        this._channel = value;
    }
}

class BistableMonostableParameters {
    private _remoteChannels: number;
    private _keyOn: number;

    constructor(remoteChannels: number, options: PairingOption) {
        this._remoteChannels = remoteChannels;
        this._keyOn = options.KeyOn;
    }

    get KeyOn(): number {
        return this._keyOn;
    }
    set KeyOn(value: number) {
        this._keyOn = value;
    }
}

class TimerParameters {
    private _remoteChannels: number;
    private _keyOn: number;
    private _timeOn: number;

    constructor(remoteChannels: number, options: PairingOption) {
        this._remoteChannels = remoteChannels;
        this._keyOn = options.KeyOn;
        this._timeOn = options.TimeOn;
    }

    get KeyOn(): number {
        return this._keyOn;
    }
    set KeyOn(value: number) {
        this._keyOn = value;

    }
    get TimeOn(): number {
        return this._timeOn;
    }
    set TimeOn(value: number) {
        this._timeOn = value;
    }
}

class DimmingBrighteningParameters {
    private _remoteChannels: number;
    private _keyOn: number;
    private _keyOff: number;
    private _keyBrightening: number;
    private _keyDimmer: number;

    constructor(remoteChannels: number, options: PairingOption) {
        this._remoteChannels = remoteChannels;
        this._keyOn = options.KeyOn;
        this._keyOff = options.KeyOff
        this._keyBrightening = options.KeyBrightening;
        this._keyDimmer = options.KeyDimmer;
    }

    get KeyOn(): number {
        return this._keyOn;
    }
    set KeyOn(value: number) {
        this._keyOn = value;
    }
    get KeyOff(): number {
        return this._keyOff;
    }
    set KeyOff(value: number) {
        this._keyOff = value;
    }
    get KeyBrightening(): number {
        return this._keyBrightening;
    }
    set KeyBrightening(value: number) {
        this._keyBrightening = value;
    }
    get KeyDimmer(): number {
        return this._keyDimmer;
    }
    set KeyDimmer(value: number) {
        this._keyDimmer = value;
    }
}

//OnOff
type OnOffModes = {
    [RemoteMode.OnOff]: OnOffParameters,
    [RemoteMode.Bistable]: BistableMonostableParameters,
    [RemoteMode.Monostable]: BistableMonostableParameters,
    [RemoteMode.Timer]: TimerParameters,
}

class OnOffOptions implements IOptions {
    ModeParams: OnOffModes

    constructor(remoteChannels: number, options: PairingOption) {
        this.ModeParams = {
            [RemoteMode.OnOff]: new OnOffParameters(remoteChannels, options),
            [RemoteMode.Bistable]: new BistableMonostableParameters(remoteChannels, options),
            [RemoteMode.Monostable]: new BistableMonostableParameters(remoteChannels, options),
            [RemoteMode.Timer]: new TimerParameters(remoteChannels, options)
        };
    }
}

//Blind
type BlindModes = {
    [RemoteMode.PixoBidi_1]: ChannelParameters,
    [RemoteMode.PixoBidi_12]: ChannelParameters,
}

class BlindOptions implements IOptions {
    ModeParams: BlindModes;

    constructor(remoteChannels: number, options: PairingOption) {
        this.ModeParams = {
            [RemoteMode.PixoBidi_1]: new ChannelParameters(remoteChannels, options),
            [RemoteMode.PixoBidi_12]: new ChannelParameters(remoteChannels, options),
        };
    }
}

//Dimmer
type DimmerModes = {
    [RemoteMode.TwoButton]: OnOffParameters,
    [RemoteMode.OneButton]: BistableMonostableParameters,
    [RemoteMode.Comfort]: DimmingBrighteningParameters,
    [RemoteMode.TimerDimmer]: TimerParameters,
}

class DimmerOptions implements IOptions {
    ModeParams: DimmerModes;

    constructor(remoteChannels: number, options: PairingOption) {
        this.ModeParams = {
            [RemoteMode.TwoButton]: new OnOffParameters(remoteChannels, options),
            [RemoteMode.OneButton]: new BistableMonostableParameters(remoteChannels, options),
            [RemoteMode.Comfort]: new DimmingBrighteningParameters(remoteChannels, options),
            [RemoteMode.TimerDimmer]: new TimerParameters(remoteChannels, options),
        };
    }
}

//Gate
type GateModes = {
    [RemoteMode.Gate]: BistableMonostableParameters;
    [RemoteMode.Bistable]: BistableMonostableParameters;
}

class GateOptions implements IOptions {
    ModeParams: GateModes;

    constructor(remoteChannels: number, options: PairingOption) {
        this.ModeParams = {
            [RemoteMode.Gate]: new BistableMonostableParameters(remoteChannels, options),
            [RemoteMode.Bistable]: new BistableMonostableParameters(remoteChannels, options),
        }
    }
}

//Gateway
type GateWayModes = {
    [RemoteMode.GateWay]: BistableMonostableParameters;
    [RemoteMode.Bistable]: BistableMonostableParameters;
}

class GateWayOptions implements IOptions {
    ModeParams: GateWayModes;

    constructor(remoteChannels: number, options: PairingOption) {
        this.ModeParams = {
            [RemoteMode.GateWay]: new BistableMonostableParameters(remoteChannels, options),
            [RemoteMode.Bistable]: new BistableMonostableParameters(remoteChannels, options),
        };
    }
}

//Facade
type FacadeModes = {
    [RemoteMode.NXZFBidi]: ChannelParameters;
    [RemoteMode.PXZFBidi]: ChannelParameters;
    [RemoteMode.WSZFBidi]: null;
}

class FacadeOptions implements IOptions {
    ModeParams: FacadeModes;

    constructor(remoteChannels: number, options: PairingOption) {
        this.ModeParams = {
            [RemoteMode.NXZFBidi]: new ChannelParameters(remoteChannels, options),
            [RemoteMode.PXZFBidi]: new ChannelParameters(remoteChannels, options),
            [RemoteMode.WSZFBidi]: null
        };
    }
}

export class RemoteDevice implements IRemoteDevice {

    private _configs: Record<string, IOptions> = {};
    protected _deviceGuid: string = "";
    protected _channel: number = 0;
    protected _guid: string = "";
    protected _name: string = "";
    protected _remoteChannelAmount: number = 0;
    protected _deviceChannelAmount: number = 0;
    protected _deviceType: DeviceType = DeviceType.OnOff;
    protected _remoteMode: RemoteMode = RemoteMode.OnOff;
    protected _currentRemotePairingOptions: DeviceTypeOptions;

    protected _rawCurrentRemoteOptions: PairingOption;
    protected _rawOldRemoteOptions: PairingOption;

    constructor(data: RemoteDeviceResponse) {
        this._deviceGuid = data.DeviceGuid;
        this._channel = data.Channel;
        this._guid = data.Guid;
        this._name = data.Name;
        this._remoteChannelAmount = data.RemoteChannelAmount;
        this._deviceChannelAmount = data.DeviceChannelAmount;
        this._deviceType = data.DeviceType;
        this._remoteMode = data.PairingOption.Mode;
        this._rawCurrentRemoteOptions = data.PairingOption;
        this._rawOldRemoteOptions = data.OldSettingsOptional;

        switch (data.DeviceType) {
            case DeviceType.OnOff:
                this._configs.OnOffOptions = new OnOffOptions(this._remoteChannelAmount, data.PairingOption);
                break;

            case DeviceType.Blind:
                this._configs.BlindOptions = new BlindOptions(this._remoteChannelAmount, data.PairingOption);
                break;

            case DeviceType.Dimmer:
                this._configs.DimmerOptions = new DimmerOptions(this._remoteChannelAmount, data.PairingOption);
                break;

            case DeviceType.Gate:
                this._configs.GateOptions = new GateOptions(this._remoteChannelAmount, data.PairingOption);
                break;

            case DeviceType.Gateway:
                this._configs.GateWayOptions = new GateWayOptions(this._remoteChannelAmount, data.PairingOption);
                break;

            case DeviceType.Facade:
                this._configs.FacadeOptions = new FacadeOptions(this._remoteChannelAmount, data.PairingOption);
                break;
        }

        this._currentRemotePairingOptions = {
            [DeviceType.OnOff]: this._configs.OnOffOptions,
            [DeviceType.Blind]: this._configs.BlindOptions,
            [DeviceType.Dimmer]: this._configs.DimmerOptions,
            [DeviceType.Gate]: this._configs.GateOptions,
            [DeviceType.Gateway]: this._configs.GateWayOptions,
            [DeviceType.Facade]: this._configs.FacadeOptions
        }
    }

    get DeviceGuid(): string {
        return this._deviceGuid;
    }
    set DeviceGuid(value: string) {
        this._deviceGuid = value;
    }
    get Channel(): number {
        return this._channel;
    }
    set Channel(value: number) {
        this._channel = value;
    }
    get Guid(): string {
        return this._guid;
    }
    set Guid(value: string) {
        this._guid = value;
    }
    get Name(): string {
        return this._name;
    }
    set Name(value: string) {
        this._name = value;
    }
    get RemoteChannelAmount(): number {
        return this._remoteChannelAmount;
    }
    get DeviceChannelAmount(): number {
        return this._deviceChannelAmount;
    }
    get DeviceType(): DeviceType {
        return this._deviceType;
    }
    set DeviceType(value: DeviceType) {
        this._deviceType = value;
    }
    get RemoteMode(): RemoteMode {
        return this._remoteMode;
    }
    set RemoteMode(value: RemoteMode) {
        this._remoteMode = value;
        this._rawCurrentRemoteOptions.Mode = value;
    }
    get CurrentRemotePairingOptions(): DeviceTypeOptions {
        return this._currentRemotePairingOptions;
    }
    get RawCurrentRemoteOptions(): PairingOption {
        return this._rawCurrentRemoteOptions;
    }
    get RawOldRemoteOptions(): PairingOption {
        return this._rawOldRemoteOptions;
    }
}